import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  EmailPageResponse,
  EmailMessageSummaryResponse,
  EmailMessageDetailResponse,
  MyEmailsSearchParams,
  ContactEmailsSearchParams,
  TimelineItemResponse,
  TimelineSearchParams,
  MyTimelineSearchParams,
  ContactInboxSyncResponse,
  InboxMessageDetailResponse,
  SendEmailDto,
  SendEmailResponse,
} from '../models/email-message.model';

/**
 * Servicio del módulo de correos enviados.
 *
 * NOTA multi-tenant: estos endpoints toman el businessId del JWT. NO se envía
 * businessId por query ni body. El interceptor de auth ya adjunta el Bearer.
 */
@Injectable({
  providedIn: 'root',
})
export class EmailMessageApiService {
  private readonly apiUrl = `${environment.apiUrl}api/emails`;

  constructor(private http: HttpClient) {}

  // GET /api/emails/mine — correos que envió el usuario autenticado.
  searchMyEmails(params: MyEmailsSearchParams): Observable<EmailPageResponse<EmailMessageSummaryResponse>> {
    let httpParams = new HttpParams();

    if (params.status !== undefined && params.status !== '') {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.purpose !== undefined && params.purpose !== '') {
      httpParams = httpParams.set('purpose', params.purpose);
    }
    if (params.idContact !== undefined) {
      httpParams = httpParams.set('idContact', params.idContact.toString());
    }
    if (params.from !== undefined && params.from !== '') {
      httpParams = httpParams.set('from', params.from);
    }
    if (params.to !== undefined && params.to !== '') {
      httpParams = httpParams.set('to', params.to);
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<EmailPageResponse<EmailMessageSummaryResponse>>(
      `${this.apiUrl}/mine`,
      { params: httpParams }
    );
  }

  // GET /api/emails/by-contact/{contactId} — historial de correos hacia un contacto (RBAC server-side).
  searchEmailsByContact(
    contactId: number,
    params: ContactEmailsSearchParams
  ): Observable<EmailPageResponse<EmailMessageSummaryResponse>> {
    let httpParams = new HttpParams();

    if (params.status !== undefined && params.status !== '') {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.from !== undefined && params.from !== '') {
      httpParams = httpParams.set('from', params.from);
    }
    if (params.to !== undefined && params.to !== '') {
      httpParams = httpParams.set('to', params.to);
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<EmailPageResponse<EmailMessageSummaryResponse>>(
      `${this.apiUrl}/by-contact/${contactId}`,
      { params: httpParams }
    );
  }

  // GET /api/emails/{id} — detalle completo de un correo.
  getEmailById(id: number): Observable<EmailMessageDetailResponse> {
    return this.http.get<EmailMessageDetailResponse>(`${this.apiUrl}/${id}`);
  }

  // GET /api/emails/timeline/by-contact/{contactId} — timeline unificado
  // (enviados + recibidos) ordenado cronológicamente DESC. Solo paginación.
  getContactTimeline(
    contactId: number,
    params: TimelineSearchParams
  ): Observable<EmailPageResponse<TimelineItemResponse>> {
    let httpParams = new HttpParams();

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<EmailPageResponse<TimelineItemResponse>>(
      `${this.apiUrl}/timeline/by-contact/${contactId}`,
      { params: httpParams }
    );
  }

  // GET /api/emails/timeline/mine — timeline GLOBAL del usuario (enviados+recibidos)
  // ordenado occurredAt DESC NULLS LAST. Paginación page/size + filtros server-side.
  getMineTimeline(
    params: MyTimelineSearchParams
  ): Observable<EmailPageResponse<TimelineItemResponse>> {
    let httpParams = new HttpParams();

    if (params.direction !== undefined) {
      httpParams = httpParams.set('direction', params.direction);
    }
    if (params.q !== undefined && params.q !== '') {
      httpParams = httpParams.set('q', params.q);
    }
    if (params.unreadOnly !== undefined) {
      httpParams = httpParams.set('unreadOnly', params.unreadOnly);
    }
    if (params.contactId !== undefined) {
      httpParams = httpParams.set('contactId', params.contactId.toString());
    }
    if (params.email !== undefined && params.email !== '') {
      httpParams = httpParams.set('email', params.email);
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }

    return this.http.get<EmailPageResponse<TimelineItemResponse>>(
      `${this.apiUrl}/timeline/mine`,
      { params: httpParams }
    );
  }

  // POST /api/emails/timeline/mine/sync — sync on-demand del buzón IMAP COMPLETO.
  // Sin body ni query params. Responde 200 SIEMPRE (synced/skipped/reason).
  syncMyInbox(): Observable<ContactInboxSyncResponse> {
    return this.http.post<ContactInboxSyncResponse>(
      `${this.apiUrl}/timeline/mine/sync`,
      null
    );
  }

  // GET /api/emails/inbox-message/{id} — detalle de un correo RECIBIDO persistido.
  // id == sourceId del ítem RECEIVED del timeline.
  getInboxMessageDetail(id: number): Observable<InboxMessageDetailResponse> {
    return this.http.get<InboxMessageDetailResponse>(`${this.apiUrl}/inbox-message/${id}`);
  }

  // POST /api/emails/send — encola un correo suelto (compose tipo Gmail).
  // El remitente y businessId salen del JWT; NO se mandan en el body.
  // Respuesta 202 ACCEPTED con el id del mensaje encolado (no es síncrono).
  sendEmail(dto: SendEmailDto): Observable<SendEmailResponse> {
    return this.http.post<SendEmailResponse>(`${this.apiUrl}/send`, dto);
  }

  // POST /api/emails/timeline/by-contact/{contactId}/sync — dispara el sync IMAP
  // on-demand del buzón del usuario para ese contacto. Sin body ni query params.
  syncContactTimeline(contactId: number): Observable<ContactInboxSyncResponse> {
    return this.http.post<ContactInboxSyncResponse>(
      `${this.apiUrl}/timeline/by-contact/${contactId}/sync`,
      null
    );
  }
}
